import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import axios from 'axios';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import FilesService, { IUploadFileUrl } from '@services/FilesService';
import { env } from 'env';
import { useTranslation } from 'react-i18next';

import { useStyles } from './styles';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import EventsService from '@services/EventsService';

type CustomSpotsEditorProps = {
  open: boolean;
  onClose: () => void;
};

const CustomSpotsEditor: React.FC<CustomSpotsEditorProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    currentRoom,
    roomSpots,
    setRoomSpots,
    selectedCustomizationUeId,
    setSelectedCustomizationUeId,
    selectedCustomizationTargetSize,
    setSelectedCustomizationTargetSize,
    setShowCustomSpotsEditor,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const classes = useStyles();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedAsset, setUploadedAsset] = useState<IUploadFileUrl | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedImageSize, setSelectedImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [isPreviewZoomOpen, setIsPreviewZoomOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const resetState = useCallback(() => {
    setIsDragging(false);
    setIsUploading(false);
    setError(null);
    setUploadedAsset(null);
    setSelectedFile(null);
    setSelectedImageSize(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  }, [previewUrl]);

  const handleFiles = useCallback(
    async (files?: FileList | null) => {
      if (!files?.length) {
        return;
      }

      const [file] = Array.from(files);
      if (!file.type.startsWith('image/')) {
        setError(t('customSpotsEditor.errorUnsupported', 'Images only are supported'));
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setError(null);
      setUploadedAsset(null);
      setSelectedFile(file);
      setSelectedImageSize(null);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [previewUrl, t],
  );

  const targetWidth = selectedCustomizationTargetSize?.width || null;
  const targetHeight = selectedCustomizationTargetSize?.height || null;
  const targetAspect =
    targetWidth && targetHeight ? targetWidth / targetHeight : 16 / 9;

  const existingSpotImageUrl = useMemo(() => {
    if (!selectedCustomizationUeId) return null;
    const spotMatch = roomSpots.find(
      (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
    );

    const url = spotMatch?.data?.url || spotMatch?.data?.location || null;
    return typeof url === 'string' && url.length ? url : null;
  }, [roomSpots, selectedCustomizationUeId]);

  const displayedPreviewUrl = previewUrl || existingSpotImageUrl || null;
  const displayedPreviewSourceLabel = previewUrl
    ? t('customSpotsEditor.previewSource.selected', {
        defaultValue: 'Selected file',
      })
    : existingSpotImageUrl
      ? t('customSpotsEditor.previewSource.current', {
          defaultValue: 'Current spot',
        })
      : null;

  const selectedImageAspect =
    selectedImageSize?.width && selectedImageSize?.height
      ? selectedImageSize.width / selectedImageSize.height
      : null;
  const isAspectMismatch =
    selectedImageAspect && targetAspect
      ? Math.abs(selectedImageAspect - targetAspect) / targetAspect > 0.02
      : false;

  const fitHintText = !isAspectMismatch
    ? null
    : t(
        'customSpotsEditor.fitHint.fill',
        'Aspect differs — image will be stretched (like in UE).',
      );

  const handleSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) return;
    if (!currentRoom) {
      setError(t('customSpotsEditor.errorNoRoom', 'Room is not available.'));
      return;
    }
    if (!selectedCustomizationUeId) {
      setError(
        t('customSpotsEditor.errorNoSelection', 'No spot selected for update.'),
      );
      return;
    }
    setIsUploading(true);
    setError(null);
    try {
      const [uploadInfo] = await FilesService.uploadFiles([selectedFile]);

      const spotMatch = roomSpots.find(
        (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
      );

      const dataPayload: any = {
        location: uploadInfo.location,
        url: uploadInfo.url,
      };
      if ((uploadInfo as any).md5) {
        dataPayload.md5 = (uploadInfo as any).md5;
      }

      const patchSpot = async () => {
        if (!spotMatch) return false;
        const spotId = spotMatch.roomSpotId || spotMatch.id;
        if (!spotId) return false;
        await axios.patch(
          `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${spotId}`,
          {
            data: dataPayload,
            isActive: true,
          },
        );
        return true;
      };

      let updatedViaPatch = false;
      try {
        updatedViaPatch = await patchSpot();
      } catch (patchError: any) {
        const status = patchError?.response?.status;
        if (status !== 404) {
          throw patchError;
        }
      }

      if (!updatedViaPatch) {
        await axios.post(
          `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots`,
          {
            name: selectedCustomizationUeId,
            type: 'IMAGE',
            ueIdentifier: selectedCustomizationUeId,
            isActive: true,
            data: dataPayload,
          },
        );
      }

      const refreshed = await EventsService.getRoomSpots(currentRoom.roomId);
      setRoomSpots(refreshed);

      const updatedSpot =
        refreshed.find(
          (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
        ) || null;

      if (updatedSpot) {
        sendMessageToUnreal({
          action: 'SPOT_UPDATED',
          spot: updatedSpot,
        });
      }

      setUploadedAsset(uploadInfo);
      setShowCustomSpotsEditor(false);
      setSelectedCustomizationUeId(null);
      setSelectedCustomizationTargetSize(null);
    } catch (uploadError) {
      setError(
        t('customSpotsEditor.errorUpload', 'Failed to upload file, please try again.'),
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    currentRoom,
    roomSpots,
    selectedCustomizationUeId,
    selectedFile,
    sendMessageToUnreal,
    setRoomSpots,
    setSelectedCustomizationUeId,
    setShowCustomSpotsEditor,
    t,
  ]);

  const handleClose = () => {
    resetState();
    setSelectedCustomizationUeId(null);
    setSelectedCustomizationTargetSize(null);
    setShowCustomSpotsEditor(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: classes.paper,
      }}
    >
      <DialogTitle sx={classes.title}>
        {t('customSpotsEditor.title', 'Custom Spots Editor')}
        <IconButton sx={classes.closeButton} onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={classes.content}>
        <Box sx={classes.metaRow}>
          <Box sx={classes.metaLeft}>
            <Typography sx={classes.metaLabel}>
              {t('customSpotsEditor.selectedSpot', 'Selected spot')}
            </Typography>
            <Typography sx={classes.metaValue}>
              {selectedCustomizationUeId || t('customSpotsEditor.none', '—')}
            </Typography>
          </Box>
          <Box sx={classes.metaRight}>
            <Typography sx={classes.metaLabel}>
              {t('customSpotsEditor.targetResolution', 'Unreal target resolution')}
            </Typography>
            <Typography sx={classes.metaValue}>
              {targetWidth && targetHeight
                ? `${targetWidth}×${targetHeight}`
                : t('customSpotsEditor.unknown', 'Unknown')}
            </Typography>
          </Box>
        </Box>

        <Box sx={classes.layout}>
          <Box sx={classes.panel}>
            <Typography sx={classes.panelTitle}>
              {t('customSpotsEditor.uploadTitle', 'Upload image')}
            </Typography>

            <Box
              sx={{
                ...classes.dropZone,
                ...(isDragging ? classes.dropZoneActive : {}),
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <UploadFileIcon sx={classes.uploadIcon} />
              <Typography variant="h6" sx={classes.dropZoneTitle}>
                {t('customSpotsEditor.dropTitle', 'Drag and drop an image')}
              </Typography>
              <Typography variant="body2" sx={classes.dropZoneSubtitle}>
                {t('customSpotsEditor.dropSubtitle', 'or choose one manually')}
              </Typography>
              <Button
                variant="contained"
                onClick={handleSelectClick}
                disabled={isUploading}
                sx={classes.selectButton}
              >
                {t('customSpotsEditor.chooseFile', 'Choose file')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => handleFiles(event.target.files)}
              />
            </Box>

            {selectedFile ? (
              <Box sx={classes.fileRow}>
                <Typography sx={classes.fileName}>{selectedFile.name}</Typography>
                <Typography sx={classes.fileMeta}>
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box sx={classes.panel}>
            <Typography sx={classes.panelTitle}>
              {t('customSpotsEditor.unrealPreviewTitle', 'Unreal preview')}
            </Typography>
            <Typography sx={classes.panelSubtitle}>
              {t(
                'customSpotsEditor.unrealPreviewSubtitle',
                'This frame matches the aspect ratio UE expects.',
              )}
            </Typography>

            <Box
              sx={{
                ...classes.unrealFrame,
                aspectRatio: `${targetAspect}`,
              }}
            >
              {displayedPreviewUrl ? (
                <IconButton
                  onClick={() => setIsPreviewZoomOpen(true)}
                  size="small"
                  sx={classes.zoomButton}
                >
                  <OpenInFullIcon fontSize="inherit" />
                </IconButton>
              ) : null}
              {displayedPreviewUrl ? (
                <Box
                  component="img"
                  src={displayedPreviewUrl}
                  alt="preview"
                  sx={{
                    ...classes.unrealImage,
                    objectFit: 'fill',
                    objectPosition: 'center',
                  }}
                  onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth && img.naturalHeight) {
                      setSelectedImageSize({
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                      });
                    }
                  }}
                />
              ) : (
                <Box sx={classes.unrealPlaceholder}>
                  <Typography sx={classes.unrealPlaceholderTitle}>
                    {t('customSpotsEditor.noPreviewTitle', 'No image selected')}
                  </Typography>
                  <Typography sx={classes.unrealPlaceholderText}>
                    {t(
                      'customSpotsEditor.noPreviewText',
                      'Choose an image to see how it will fit.',
                    )}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={classes.previewMetaRow}>
              <Typography sx={classes.previewMetaText}>
                {targetWidth && targetHeight
                  ? t('customSpotsEditor.targetLabel', {
                      defaultValue: 'Target: {{w}}×{{h}}',
                      w: targetWidth,
                      h: targetHeight,
                    })
                  : t('customSpotsEditor.targetLabelUnknown', 'Target: unknown')}
              </Typography>
              {displayedPreviewSourceLabel ? (
                <Typography sx={classes.previewMetaText}>
                  {displayedPreviewSourceLabel}
                </Typography>
              ) : null}
              {selectedImageSize ? (
                <Typography sx={classes.previewMetaText}>
                  {t('customSpotsEditor.imageLabel', {
                    defaultValue: 'Image: {{w}}×{{h}}',
                    w: selectedImageSize.width,
                    h: selectedImageSize.height,
                  })}
                </Typography>
              ) : null}
            </Box>

            {fitHintText ? (
              <Typography sx={classes.aspectWarning}>
                {fitHintText}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Dialog
          open={isPreviewZoomOpen}
          onClose={() => setIsPreviewZoomOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: classes.zoomPaper,
          }}
        >
          <DialogTitle sx={classes.zoomTitle}>
            {t('customSpotsEditor.zoomTitle', 'Unreal preview')}
            <IconButton
              sx={classes.closeButton}
              onClick={() => setIsPreviewZoomOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={classes.zoomContent}>
            <Box sx={classes.zoomMetaRow}>
              <Typography sx={classes.previewMetaText}>
                {selectedCustomizationUeId
                  ? t('customSpotsEditor.selectedSpot', {
                      defaultValue: 'Selected spot: {{id}}',
                      id: selectedCustomizationUeId,
                    })
                  : null}
              </Typography>
              <Typography sx={classes.previewMetaText}>
                {targetWidth && targetHeight
                  ? t('customSpotsEditor.targetLabel', {
                      defaultValue: 'Target: {{w}}×{{h}}',
                      w: targetWidth,
                      h: targetHeight,
                    })
                  : t('customSpotsEditor.targetLabelUnknown', 'Target: unknown')}
              </Typography>
              {selectedImageSize ? (
                <Typography sx={classes.previewMetaText}>
                  {t('customSpotsEditor.imageLabel', {
                    defaultValue: 'Image: {{w}}×{{h}}',
                    w: selectedImageSize.width,
                    h: selectedImageSize.height,
                  })}
                </Typography>
              ) : null}
            </Box>

            <Box sx={classes.zoomFrameWrap}>
              <Box
                sx={{
                  ...classes.unrealFrame,
                  ...classes.zoomFrame,
                  aspectRatio: `${targetAspect}`,
                }}
              >
                {displayedPreviewUrl ? (
                  <Box
                    component="img"
                    src={displayedPreviewUrl}
                    alt="preview"
                    sx={{
                      ...classes.unrealImage,
                      objectFit: 'fill',
                      objectPosition: 'center',
                    }}
                  />
                ) : null}
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        {uploadedAsset ? (
          <Box sx={classes.previewRow}>
            <Typography sx={classes.statusText}>
              {t('customSpotsEditor.uploaded', 'Uploaded')}: {uploadedAsset.name}
            </Typography>
            <Typography sx={classes.previewText}>
              {uploadedAsset.url}
            </Typography>
          </Box>
        ) : null}

        {error ? (
          <Typography sx={classes.errorText} variant="body2">
            {error}
          </Typography>
        ) : null}

        {isUploading ? (
          <Box sx={classes.statusRow}>
            <CircularProgress size={20} />
            <Typography sx={classes.statusText}>
              {t(
                'customSpotsEditor.uploading',
                'Uploading file and updating spot...',
              )}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={classes.actions}>
        <Button
          variant="text"
          color="inherit"
          disabled={isUploading}
          onClick={handleClose}
          sx={classes.cancelButton}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedFile || isUploading}
          onClick={handleConfirm}
          sx={classes.confirmButton}
        >
          {t('customSpotsEditor.confirm', 'Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomSpotsEditor;

